import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import { List, ListColumn } from '~/components/common/List'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import { Typography } from '~/components/common/Typography'
import { uploadMetadataFile } from '~/services/organizationService'
import { BetterToast } from '~/utils/toast'

export type MetadataFileStatus = 'pending' | 'uploading' | 'completed' | 'error'

export interface UploadedMetadataFile {
  id: number
  name: string
  size: string
  file?: File
  uploadStatus?: MetadataFileStatus
  s3Path?: string
  fileExt?: string
}

interface MetadataFileSelectModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: { uploadedFiles: UploadedMetadataFile[] }) => void
  workspaceUuid?: string
  initialFiles?: UploadedMetadataFile[]
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

/**
 * Allowed file extensions for mule refactor metadata uploads.
 * This is intentionally restricted to text-based files for this specific modal.
 * The general context upload (useContextUpload.ts) allows many more formats.
 */
const ALLOWED_MULE_REFACTOR_EXTENSIONS = new Set(['.txt', '.md'])

/**
 * Check if a file extension is allowed for mule refactor metadata uploads
 */
const isAllowedMuleRefactorExtension = (filename: string): boolean => {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1) return false
  const extension = filename.slice(lastDotIndex).toLowerCase()
  return ALLOWED_MULE_REFACTOR_EXTENSIONS.has(extension)
}

// Loading spinner component (blue spinner)
const LoadingSpinner = () => (
  <svg
    className='animate-spin h-4 w-4 text-blue-500'
    xmlns='http://www.w3.org/2000/svg'
    fill='none'
    viewBox='0 0 24 24'
  >
    <circle
      className='opacity-25'
      cx='12'
      cy='12'
      r='10'
      stroke='currentColor'
      strokeWidth='4'
    ></circle>
    <path
      className='opacity-75'
      fill='currentColor'
      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
    ></path>
  </svg>
)

// Checkmark icon for completed uploads
const CheckmarkIcon = () => (
  <svg
    className='h-4 w-4 text-green-500'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 20 20'
    fill='currentColor'
  >
    <path
      fillRule='evenodd'
      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
      clipRule='evenodd'
    />
  </svg>
)

// Error icon for failed uploads
const ErrorIcon = () => (
  <svg
    className='h-4 w-4 text-red-500'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 20 20'
    fill='currentColor'
  >
    <path
      fillRule='evenodd'
      d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
      clipRule='evenodd'
    />
  </svg>
)

// Uploaded Files List Component
interface UploadedFilesListProps {
  uploadedFiles: UploadedMetadataFile[]
  setUploadedFiles: (files: UploadedMetadataFile[]) => void
}

const UploadedFilesList: React.FC<UploadedFilesListProps> = ({
  uploadedFiles,
  setUploadedFiles
}) => {
  if (uploadedFiles.length === 0) return null

  const columns: ListColumn[] = [
    {
      label: 'File',
      flex: 1,
      align: 'left'
    },
    {
      label: '',
      width: '80px',
      align: 'right'
    }
  ]

  const values = uploadedFiles.map(file => [
    <div key={`file-${file.id}`} className='flex flex-col gap-1 min-w-0'>
      <div className='flex items-center gap-2'>
        <Typography
          fontFamily='geist'
          fontSize='bodyMdMedium'
          fontWeight='medium'
          className='text-tokens-neutral-text-primary truncate'
        >
          {file.name}
        </Typography>
        {/* Upload status indicator */}
        {file.uploadStatus === 'uploading' && <LoadingSpinner />}
        {(file.uploadStatus === 'completed' || (!file.uploadStatus && file.s3Path)) && (
          <CheckmarkIcon />
        )}
        {file.uploadStatus === 'error' && <ErrorIcon />}
      </div>
      <div className='flex items-center gap-2'>
        <Typography
          fontFamily='geistMono'
          fontSize='labelSm'
          fontWeight='semibold'
          className='text-tokens-neutral-text-tertiary'
        >
          {file.size}
        </Typography>
        {file.uploadStatus === 'uploading' && (
          <Typography fontFamily='geistMono' fontSize='labelSm' className='text-blue-500'>
            Uploading...
          </Typography>
        )}
        {file.uploadStatus === 'error' && (
          <Typography fontFamily='geistMono' fontSize='labelSm' className='text-red-500'>
            Upload failed
          </Typography>
        )}
      </div>
    </div>,
    <button
      key={`remove-${file.id}`}
      onClick={() => {
        setUploadedFiles(uploadedFiles.filter(f => f.id !== file.id))
      }}
      className='flex w-[36px] h-[36px] p-0 items-center justify-center rounded-lg hover:bg-[rgba(0,0,0,0.04)] transition-colors'
      title='Remove'
    >
      <svg
        width='20'
        height='20'
        viewBox='0 0 20 20'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M7.03125 3.59375H6.875C6.96094 3.59375 7.03125 3.52344 7.03125 3.4375V3.59375H12.9688V3.4375C12.9688 3.52344 13.0391 3.59375 13.125 3.59375H12.9688V5H14.375V3.4375C14.375 2.74805 13.8145 2.1875 13.125 2.1875H6.875C6.18555 2.1875 5.625 2.74805 5.625 3.4375V5H7.03125V3.59375ZM16.875 5H3.125C2.7793 5 2.5 5.2793 2.5 5.625V6.25C2.5 6.33594 2.57031 6.40625 2.65625 6.40625H3.83594L4.31836 16.6211C4.34961 17.2871 4.90039 17.8125 5.56641 17.8125H14.4336C15.1016 17.8125 15.6504 17.2891 15.6816 16.6211L16.1641 6.40625H17.3438C17.4297 6.40625 17.5 6.33594 17.5 6.25V5.625C17.5 5.2793 17.2207 5 16.875 5ZM14.2832 16.4062H5.7168L5.24414 6.40625H14.7559L14.2832 16.4062Z'
          fill='black'
          fillOpacity='0.45'
        />
      </svg>
    </button>
  ])

  return (
    <div className='mb-4'>
      <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='medium' className='mb-2'>
        Uploaded Files ({uploadedFiles.length})
      </Typography>
      <List columns={columns} values={values} />
    </div>
  )
}

export const MetadataFileSelectModal: React.FC<MetadataFileSelectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  workspaceUuid,
  initialFiles = []
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedMetadataFile[]>(initialFiles)
  const isMountedRef = useRef(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const prevIsOpenRef = useRef(isOpen)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Sync state when modal opens (transitions from closed to open)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setUploadedFiles(initialFiles)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, initialFiles])

  // Upload handler - uploads immediately upon file selection
  const startUpload = useCallback(
    (fileId: number, file: File) => {
      if (!workspaceUuid) return

      uploadMetadataFile(workspaceUuid, file, {
        onComplete: (s3Path: string) => {
          if (isMountedRef.current) {
            setUploadedFiles(prev =>
              prev.map(f =>
                f.id === fileId
                  ? { ...f, uploadStatus: 'completed' as MetadataFileStatus, s3Path }
                  : f
              )
            )
          }
        },
        onError: (error: string) => {
          if (isMountedRef.current) {
            setUploadedFiles(prev =>
              prev.map(f =>
                f.id === fileId ? { ...f, uploadStatus: 'error' as MetadataFileStatus } : f
              )
            )
            BetterToast.error(`Upload failed: ${error}`)
          }
        }
      })
    },
    [workspaceUuid]
  )

  // File selection handler
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files || files.length === 0) return

      const newFiles: UploadedMetadataFile[] = []
      const rejectedFiles: string[] = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]

        // Validate file extension - only .txt and .md allowed for mule refactor
        if (!isAllowedMuleRefactorExtension(file.name)) {
          const ext = file.name.includes('.')
            ? file.name.slice(file.name.lastIndexOf('.'))
            : 'no extension'
          rejectedFiles.push(`${file.name} (${ext})`)
          continue
        }

        const fileId = Date.now() + i

        const newFile: UploadedMetadataFile = {
          id: fileId,
          name: file.name,
          size: formatFileSize(file.size),
          file,
          fileExt: file.name.split('.').pop() || '',
          uploadStatus: workspaceUuid ? 'uploading' : 'pending'
        }
        newFiles.push(newFile)

        // Start upload immediately if workspaceUuid provided
        if (workspaceUuid) {
          startUpload(fileId, file)
        }
      }

      // Show error toast for rejected files
      if (rejectedFiles.length > 0) {
        const message =
          rejectedFiles.length === 1
            ? `File type not supported: ${rejectedFiles[0]}. Only .txt and .md files are allowed.`
            : `${rejectedFiles.length} files have unsupported types. Only .txt and .md files are allowed.`
        BetterToast.error(message)
      }

      if (newFiles.length > 0) {
        setUploadedFiles(prev => [...prev, ...newFiles])
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [workspaceUuid, startUpload]
  )

  // Check if any uploads are in progress
  const hasUploadsInProgress = uploadedFiles.some(f => f.uploadStatus === 'uploading')

  const handleConfirm = useCallback(() => {
    if (hasUploadsInProgress) return
    onConfirm({ uploadedFiles })
    onClose()
  }, [hasUploadsInProgress, onConfirm, uploadedFiles, onClose])

  const handleSelectFiles = () => {
    fileInputRef.current?.click()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton={hasUploadsInProgress}
      disableOutsideClick={hasUploadsInProgress}
      className='w-[640px] h-[629px]'
    >
      <div className='h-full flex flex-col'>
        <div className='flex items-center justify-between px-6 py-4 border-b border-tokens-neutral-border-2'>
          <Typography
            fontFamily='geist'
            fontSize='titleSm'
            fontWeight='bold'
            className='text-tokens-neutral-text-primary'
          >
            Select Metadata Files
          </Typography>
        </div>

        <ModalBody className='px-[60px] flex-1 overflow-y-auto'>
          <div className='py-4'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='text-tokens-neutral-text-secondary mb-4'
            >
              Upload metadata files (text or markdown) to provide additional context of the legacy
              repository.
            </Typography>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type='file'
              multiple
              accept='.txt,.md'
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* Upload area styled exactly like UploadFile component */}
            <div
              className='flex rounded-[16px] border border-dashed border-[#EAEAEA] bg-[rgba(189,189,189,0.08)] transition-colors w-full h-[72px] p-[16px] cursor-pointer hover:bg-[rgba(189,189,189,0.16)]'
              onClick={handleSelectFiles}
            >
              <div className='flex items-center gap-[16px] w-full h-full'>
                <div className='w-[24px] h-[24px] flex items-center justify-center shrink-0 text-[rgba(0,0,0,0.45)]'>
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M12 15V3M12 3L8 7M12 3L16 7'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M2 17L2 19C2 20.1046 2.89543 21 4 21L20 21C21.1046 21 22 20.1046 22 19V17'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='flex flex-col gap-[4px] flex-1 justify-center'>
                  <div className='font-geist text-[14px] font-medium leading-[22px] text-[#5B5BD5]'>
                    Upload Files
                  </div>
                  <div className='font-geist text-[11px] font-normal leading-normal text-[rgba(0,0,0,0.45)]'>
                    Select files to upload (.txt, .md)
                  </div>
                </div>
              </div>
            </div>

            {/* File list with status indicators */}
            {uploadedFiles.length > 0 && (
              <div className='mt-6'>
                <UploadedFilesList
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                />
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant='outlined' onClick={onClose} isDisabled={hasUploadsInProgress}>
            Cancel
          </Button>
          <Button variant='primary' isDisabled={hasUploadsInProgress} onClick={handleConfirm}>
            {hasUploadsInProgress ? 'Uploading...' : 'Confirm'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  )
}
